import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Store, Clapperboard, Wallet, ShieldCheck } from 'lucide-react';
import Tooltip from '../ui/Tooltip';
import { TYPE } from '../../constants/typeScale';

/**
 * THE ACTIVITY BAR — the workstation's top-level menu, as a column of icons on the far left.
 *
 * Owner 2026-08-23, screenshot98: *"maybe our high level menu can be a vertical set of icons on far left,
 * left of left pane, just like vscode"*, and *"move the user profile and bar to the far left icon column,
 * keep the left pane cleaner for the files explorer."*
 *
 * Two things follow from copying that idiom properly rather than only its shape:
 *
 *  1. **It is always there.** VS Code's activity bar does not collapse, which is why folding the sidebar can
 *     never strand you — the way back is a fixed 48px column, not a control that lives inside the thing you
 *     folded. That makes it the structural answer to the pane trap (BUGS.md 2026-08-23), so the left pane no
 *     longer leaves its own rail behind: this IS the rail, and it is the same one at every level.
 *  2. **Clicking it always ends with the workstation VISIBLE.** VS Code toggles the sidebar when you click the
 *     active view, and that is the behaviour once everything is already open — but if anything is folded, the
 *     click UNFOLDS rather than toggling. A creator pressing "your productions" is asking to see their work,
 *     never to hide more of it.
 *
 * NO CREW ICON HERE (owner 2026-08-24). The crew pane carries its own toggle — its collapse control when
 * open, its expand rail when folded — and both are structural now, so a second control on this column would
 * be a duplicate rather than a safety net. The bar is for TOP-LEVEL destinations; the crew is not a
 * destination, it is who is making the thing you already have open.
 *
 * EVERY CONTROL HERE NAMES ITSELF ON HOVER (owner law 2026-08-24: *"need hover-over tooltip whenever we have
 * controls, icons"*). A column of glyphs is unreadable until you have learned it, and the native `title=` is
 * too slow and too ugly to count — so these use the app's own `Tooltip`, which opens on hover, on keyboard
 * focus, and on tap.
 *
 * Identity sits at the BOTTOM, away from the work — the same place VS Code keeps the account. It used to be a
 * two-row footer inside the 272px explorer, which put the creator's own name and a theme switch in the middle
 * of their productions.
 *
 * NAVIGATION REVIEW (owner 2026-09-26). Three changes, each answering a finding:
 *  - **Every destination carries its WORD under the icon**, not only on hover. A column of bare glyphs had to
 *    be learned before it could be used; a tooltip names an icon once you are already guessing.
 *  - **The order is the journey**: make, then sell, then pay. The store used to sit on top (2026-08-24), which
 *    put the last step of the journey first for a creator who has nothing to sell yet.
 *  - **Settings is one door**, a row in the account menu. The separate gear duplicated "Your profile" in that
 *    same menu, so the rail carried two icons for one page.
 */

export interface ActivityBarProps {
  /** Is the explorer pane open? Drives the pressed state only — what the click DOES is the shell's call. */
  explorerOpen: boolean;
  /**
   * Show me my workstation. The shell decides what that means from the route and BOTH panes' state, because
   * this button's job is "get me back to my work" — a bar that can leave a creator looking at a canvas with
   * no panes has failed at the one thing it exists for.
   */
  onProductions: () => void;
  /** True while on a Studio route — the explorer view is the active one there. */
  onStudio: boolean;
  /**
   * Which destination the creator is standing in, so the bar says where they are (owner 2026-08-24: the
   * credits icon *"not staying highlighted"*). VS Code marks the active view whether or not its pane is open;
   * the mark answers "where am I?", which is a different question from "is the sidebar showing?".
   */
  activeView?: 'store' | 'productions' | 'wallet' | 'admin' | 'settings';
  isAdmin?: boolean;
  /** The creator's avatar image, if they have one; otherwise their initial is drawn. */
  avatar?: string;
  /** Display name/alias/email — for the initial and the button's label. */
  name: string;
  /**
   * The one signal a folded explorer must not swallow — an in-flight render. Passed in as a node (the
   * existing `RenderTracker dot`) rather than a boolean so the polling stays in the component that owns it.
   */
  renderSignal?: React.ReactNode;
  /** The account menu's contents (the existing SidebarFooter) — passed in so identity logic stays in one place. */
  account: React.ReactNode;
}

const ITEM = `flex w-11 shrink-0 flex-col items-center gap-0.5 rounded-lg py-1.5 text-secondary-400 transition-colors
              hover:bg-secondary-100 hover:text-secondary-800 dark:hover:bg-secondary-800 dark:hover:text-secondary-100`;
const ITEM_ON = `flex w-11 shrink-0 flex-col items-center gap-0.5 rounded-lg py-1.5 bg-secondary-100 text-secondary-900
                 transition-colors dark:bg-secondary-800 dark:text-white`;
const ICON = `flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-secondary-400 transition-colors
              hover:bg-secondary-100 hover:text-secondary-800 dark:hover:bg-secondary-800 dark:hover:text-secondary-100`;
const ICON_ON = `flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-secondary-100 text-secondary-900
                 transition-colors dark:bg-secondary-800 dark:text-white`;

interface RailItemProps {
  /** The word under the icon — short enough to fit the 48px column at the 10px floor. */
  label: string;
  /** The fuller name, spoken to assistive tech and shown on hover. */
  name: string;
  icon: React.ReactNode;
  active: boolean;
  onClick: () => void;
  testId?: string;
  expanded?: boolean;
}

/** One labelled destination on the rail. */
const RailItem: React.FC<RailItemProps> = ({ label, name, icon, active, onClick, testId, expanded }) => (
  <Tooltip content={name} side="bottom">
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      aria-label={name}
      aria-current={active ? 'page' : undefined}
      aria-expanded={expanded}
      className={active ? ITEM_ON : ITEM}
    >
      {icon}
      <span aria-hidden className={`${TYPE.micro} leading-none`}>{label}</span>
    </button>
  </Tooltip>
);

const ActivityBar: React.FC<ActivityBarProps> = ({
  explorerOpen, onProductions, onStudio, activeView, isAdmin, avatar, name, renderSignal, account,
}) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [menu, setMenu] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const initial = (name || 'A').trim().charAt(0).toUpperCase();

  // Dismiss the account menu like every other popover in the app: click away, or Escape.
  useEffect(() => {
    if (!menu) return;
    const away = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setMenu(false); };
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenu(false); };
    document.addEventListener('mousedown', away, true);
    document.addEventListener('keydown', key);
    return () => { document.removeEventListener('mousedown', away, true); document.removeEventListener('keydown', key); };
  }, [menu]);

  // A row in the menu that navigates (Settings) must not leave the menu hanging open over the new page.
  useEffect(() => { setMenu(false); }, [pathname]);

  return (
    <nav
      data-testid="activity-bar"
      aria-label="Main"
      className="hidden h-full w-12 shrink-0 flex-col items-center gap-1 border-r border-secondary-200
                 bg-white py-2 dark:border-secondary-600 dark:bg-black md:flex"
    >
      <div className="relative">
        <RailItem
          label="Make"
          name="Your series"
          icon={<Clapperboard className="h-5 w-5" />}
          active={activeView === 'productions'}
          onClick={onProductions}
          testId="activity-productions"
          expanded={onStudio ? explorerOpen : undefined}
        />
        {/* A 20-40 minute render must not become invisible because a pane was folded (screenshot79's rule).
            The explorer carries the full chip; when it is away, the signal survives here. */}
        {!explorerOpen && renderSignal && (
          <span className="pointer-events-none absolute right-0.5 top-0.5">{renderSignal}</span>
        )}
      </div>

      <RailItem
        label="Store"
        name="Your store"
        icon={<Store className="h-5 w-5" />}
        active={activeView === 'store'}
        onClick={() => navigate('/store')}
        testId="activity-store"
      />

      <RailItem
        label="Credits"
        name="Your credits"
        icon={<Wallet className="h-5 w-5" />}
        active={activeView === 'wallet'}
        onClick={() => navigate('/wallet')}
        testId="activity-credits"
      />

      {isAdmin && (
        <RailItem
          label="Admin"
          name="Admin"
          icon={<ShieldCheck className="h-5 w-5" />}
          active={activeView === 'admin'}
          onClick={() => navigate('/admin')}
        />
      )}

      {/* IDENTITY LIVES AT THE BOTTOM, out of the work's way. Settings is a row inside this menu, so the avatar
          carries the "you are here" mark while the creator is on the settings page. */}
      <div ref={ref} className="relative mt-auto flex flex-col items-center gap-1">
        {menu && (
          /* The menu opens BESIDE the bar rather than over the canvas, so the column it belongs to stays
             visible — the same reason VS Code anchors its account menu to the gear. */
          <div
            data-testid="account-menu"
            className="absolute bottom-0 left-11 z-50 w-64 rounded-xl border border-secondary-200 bg-white p-3
                       shadow-xl dark:border-secondary-600 dark:bg-secondary-900"
          >
            {account}
          </div>
        )}
        <Tooltip content={name || 'Your account'}>
          <button
            type="button"
            onClick={() => setMenu((v) => !v)}
            data-testid="activity-account"
            aria-label="Your account"
            aria-expanded={menu}
            aria-current={activeView === 'settings' ? 'page' : undefined}
            className={menu || activeView === 'settings' ? ICON_ON : ICON}
          >
            {avatar
              ? <img src={avatar} alt="" className="h-7 w-7 rounded-full object-cover" />
              : (
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-secondary-100 text-[11px]
                                 font-semibold text-secondary-600 dark:bg-secondary-800 dark:text-secondary-300">
                  {initial}
                </span>
              )}
          </button>
        </Tooltip>
      </div>
    </nav>
  );
};

export default ActivityBar;
